import { useMemo } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Skeleton } from '@/ui/Skeleton';
import { Drawer } from '@/ui/Drawer';
import { Avatar } from '@/components/Avatar';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import {
  etiquetaDelValor, useEncuesta, useParticipantes, useResultados,
  type FilaDeResultado, type Pregunta,
} from './api';

/**
 * Resultados — lo que respondió el equipo. Solo `encuestas:manage`.
 *
 * ## Lo que cambia
 *
 * - **La participación tiene su propio estado.** El vanilla la pedía con
 *   `.catch(() => null)`: si fallaba, la sección entera desaparecía y nadie se
 *   enteraba de que había un dato que no se pudo leer.
 * - **Los gráficos ya no llevan una paleta escrita a mano** —diez colores, con
 *   el navy y el naranja de la marca copiados en hexadecimal—. Una barra por
 *   opción, del color de la marca, con su número y su porcentaje escritos: el
 *   color no era el que informaba.
 * - **Una escala y un ánimo dicen su promedio.** Es el número que se busca en
 *   una escala de 1 a 10, y había que calcularlo a mano mirando las barras.
 * - Era un `Utils.modal` con todo dentro; ahora es un panel, que cabe en el
 *   móvil y no tapa la lista.
 */
export function Resultados({
  encuestaId, onCerrar,
}: {
  encuestaId: number;
  onCerrar: () => void;
}) {
  const encuesta = useEncuesta(encuestaId);
  const resultados = useResultados(encuestaId);
  const participacion = useParticipantes(encuestaId);

  const preguntas = useMemo(
    () => [...(encuesta.data?.questions ?? [])].sort((a, b) => a.order - b.order),
    [encuesta.data],
  );
  const filas = resultados.data?.responses ?? [];
  const anonima = resultados.data?.is_anonymous !== false;

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={encuesta.data?.title ? `Resultados — ${encuesta.data.title}` : 'Resultados'}
      descripcion="Lo que respondió el equipo, y quién participó."
      ancho="lg"
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      {resultados.isPending || encuesta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : resultados.isError || encuesta.isError ? (
        <EstadoError
          err={resultados.error ?? encuesta.error}
          que="los resultados"
          onReintentar={() => { void resultados.refetch(); void encuesta.refetch(); }}
        />
      ) : (
        <div className="flex flex-col gap-6">
          <header className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-sunken p-3">
            <span className="grid size-11 shrink-0 place-items-center rounded-full bg-brand/10 text-5 text-brand-ink">
              <i className="bi bi-clipboard-data" aria-hidden="true" />
            </span>
            <span>
              <strong className="block text-7 font-extrabold leading-none tabular-nums text-ink">
                {resultados.data?.total_responses ?? 0}
              </strong>
              <span className="mt-1 block text-2 text-muted">
                {resultados.data?.total_responses === 1 ? 'respuesta' : 'respuestas'}
              </span>
            </span>
            <Badge tone={anonima ? 'info' : 'warn'} className="ml-auto">
              <i className={anonima ? 'bi bi-shield-lock' : 'bi bi-person-vcard'} aria-hidden="true" />
              {anonima ? 'Anónima' : 'Identificada'}
            </Badge>
          </header>

          <Participacion consulta={participacion} anonima={anonima} />

          <section aria-labelledby="res-preguntas">
            <h3 id="res-preguntas" className="mb-3 text-4 font-bold text-brand-ink">
              <i className="bi bi-bar-chart-line-fill" aria-hidden="true" /> Por pregunta
            </h3>
            {!filas.length ? (
              <p className="rounded-sm border border-line bg-sunken p-3 text-3 text-ink-2">
                <i className="bi bi-info-circle" aria-hidden="true" /> Todavía no hay respuestas.
              </p>
            ) : (
              <div className="flex flex-col gap-5">
                {preguntas.map((q) => (
                  <ResultadoDePregunta key={q.id} pregunta={q} filas={filas} />
                ))}
              </div>
            )}
          </section>

          {!anonima && filas.length > 0 && (
            <InformeDeEncuestados preguntas={preguntas} filas={filas} />
          )}

          <p className="flex items-start gap-2 rounded-sm border border-line bg-sunken p-3 text-2 text-ink-2">
            <i
              className={cn('bi mt-0.5 shrink-0', anonima ? 'bi-shield-lock' : 'bi-person-vcard')}
              aria-hidden="true"
            />
            {anonima
              ? 'Ves quién participó, pero no qué respondió cada quien: las respuestas llegan barajadas y sin hora, para que el orden tampoco lo delate.'
              : 'Encuesta identificada: cada respuesta está vinculada a su autor, y así se le dijo al responder.'}
          </p>
        </div>
      )}
    </Drawer>
  );
}

/* ── Participación ─────────────────────────────────────────────────────── */

function Participacion({
  consulta, anonima,
}: {
  consulta: ReturnType<typeof useParticipantes>;
  anonima: boolean;
}) {
  return (
    <section aria-labelledby="res-participacion">
      <h3 id="res-participacion" className="mb-3 text-4 font-bold text-brand-ink">
        <i className="bi bi-people-fill" aria-hidden="true" /> Participación
      </h3>
      {consulta.isPending ? (
        <Skeleton className="h-20 w-full" />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="la participación"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !consulta.data ? null : (
        <ParticipacionLeida datos={consulta.data} anonima={anonima} />
      )}
    </section>
  );
}

function ParticipacionLeida({
  datos, anonima,
}: {
  datos: NonNullable<ReturnType<typeof useParticipantes>['data']>;
  anonima: boolean;
}) {
  const pct = datos.total_active
    ? Math.round((datos.responded_count / datos.total_active) * 100)
    : 0;
  return (
    <>
      <p className="mb-1 flex flex-wrap items-baseline gap-x-2 text-3">
        <strong className="tabular-nums text-ink">
          {datos.responded_count} de {datos.total_active}
        </strong>
        <span className="text-muted">personas · {pct} %</span>
      </p>
      <BarraDeProgreso pct={pct} etiqueta="Participación en la encuesta" />

      {datos.by_area.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {datos.by_area.map((a) => {
            const suyo = a.total ? Math.round((a.responded / a.total) * 100) : 0;
            return (
              <li key={a.area}>
                <p className="mb-0.5 flex justify-between text-2">
                  <span className="min-w-0 truncate text-ink-2">{a.area}</span>
                  <strong className="tabular-nums text-ink-2">{a.responded}/{a.total}</strong>
                </p>
                <BarraDeProgreso pct={suyo} etiqueta={`Participación de ${a.area}`} />
              </li>
            );
          })}
        </ul>
      )}

      {datos.participants.length > 0 ? (
        <ul className="mt-3 flex max-h-56 flex-col gap-1.5 overflow-y-auto">
          {datos.participants.map((p) => (
            <li key={p.user_id} className="flex items-center gap-2.5">
              <Avatar nombre={p.full_name} tamano={30} />
              <span className="min-w-0 flex-1 truncate text-3">
                {p.full_name}
                {p.area_name && <span className="text-2 text-muted"> · {p.area_name}</span>}
              </span>
              <span className="shrink-0 text-1 text-muted">{Utils.fmtDate(p.responded_at)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-3 text-muted">Todavía no ha respondido nadie.</p>
      )}

      {anonima && (
        <p className="mt-2 text-1 text-muted">
          Participar y responder son dos listas distintas: esta dice quién, y los resultados
          qué, sin poder cruzarlas.
        </p>
      )}
    </>
  );
}

/* ── Resultados de una pregunta ────────────────────────────────────────── */

/** El recuento de una pregunta: cuántas veces se eligió cada valor. */
function contar(pregunta: Pregunta, filas: FilaDeResultado[]) {
  const cuenta = new Map<string, number>();
  for (const fila of filas) {
    const respuesta = (fila.answers ?? []).find((a) => a.question_id === pregunta.id);
    if (!respuesta) continue;
    const valores = Array.isArray(respuesta.answer_value)
      ? respuesta.answer_value
      : [respuesta.answer_value];
    for (const v of valores) {
      const clave = String(v ?? '').trim();
      if (!clave) continue;
      cuenta.set(clave, (cuenta.get(clave) ?? 0) + 1);
    }
  }
  return cuenta;
}

function ResultadoDePregunta({ pregunta, filas }: { pregunta: Pregunta; filas: FilaDeResultado[] }) {
  if (pregunta.question_type === 'open_text') {
    const textos = filas
      .map((f) => (f.answers ?? []).find((a) => a.question_id === pregunta.id)?.answer_value)
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '');
    return (
      <div>
        <p className="mb-2 text-3 font-bold text-brand-ink">{pregunta.question_text}</p>
        {textos.length ? (
          <ul className="flex max-h-44 flex-col gap-1.5 overflow-y-auto">
            {textos.map((t, i) => (
              <li
                key={i}
                className="rounded-sm border-l-2 border-accent bg-sunken px-3 py-1.5 text-2 text-ink-2"
              >
                {t}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-3 text-muted">Sin respuestas.</p>
        )}
      </div>
    );
  }

  const cuenta = contar(pregunta, filas);
  const porEscala = pregunta.question_type === 'scale' || pregunta.question_type === 'emoji_mood';
  const datos = [...cuenta.entries()]
    .sort((a, b) => (porEscala ? Number(a[0]) - Number(b[0]) : b[1] - a[1]))
    .map(([clave, valor]) => ({ etiqueta: etiquetaDelValor(pregunta, clave), valor }));
  const total = datos.reduce((s, d) => s + d.valor, 0);

  // El promedio de una escala es EL número que se busca, y había que sacarlo a
  // ojo mirando las barras.
  const promedio = porEscala && total
    ? [...cuenta.entries()].reduce((s, [k, v]) => s + Number(k) * v, 0) / total
    : null;

  return (
    <div>
      <p className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 text-3 font-bold text-brand-ink">
        <span className="min-w-0">{pregunta.question_text}</span>
        {promedio != null && (
          <span className="shrink-0 text-2 font-semibold text-ink-2">
            Promedio <strong className="tabular-nums text-brand-ink">
              {promedio.toLocaleString('es-CO', { maximumFractionDigits: 1 })}
            </strong>
          </span>
        )}
      </p>
      {datos.length ? (
        <GraficoDeBarras datos={datos} total={total} />
      ) : (
        <p className="text-3 text-muted">Sin respuestas.</p>
      )}
    </div>
  );
}

/** Una barra por valor, con su número y su porcentaje escritos.
 *
 *  Sustituye a `Utils.barChart`, del que esta pantalla era el ÚNICO consumidor
 *  del portal. Crece con `transform` y no con `width`, como todas las barras
 *  del proyecto desde el 2026-09-01: animar `width` obliga al navegador a
 *  rehacer la maqueta en cada fotograma. */
export function GraficoDeBarras({
  datos, total,
}: {
  datos: Array<{ etiqueta: string; valor: number }>;
  total: number;
}) {
  const maximo = Math.max(...datos.map((d) => d.valor), 1);
  return (
    <ul className="flex flex-col gap-2">
      {datos.map((d, i) => {
        const pct = total ? Math.round((d.valor / total) * 100) : 0;
        return (
          <li key={`${d.etiqueta}-${i}`}>
            <p className="mb-0.5 flex justify-between gap-2 text-2">
              <span className="min-w-0 truncate text-ink-2">{d.etiqueta}</span>
              <strong className="shrink-0 tabular-nums text-brand-ink">
                {d.valor} · {pct} %
              </strong>
            </p>
            <div className="h-3 overflow-hidden rounded-sm bg-sunken">
              <div
                className="h-full origin-left bg-brand transition-transform duration-500 motion-reduce:transition-none"
                style={{ transform: `scaleX(${Math.max(d.valor / maximo, 0.02)})` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* ── Informe de encuestados (solo identificadas) ───────────────────────── */

function InformeDeEncuestados({
  preguntas, filas,
}: {
  preguntas: Pregunta[];
  filas: FilaDeResultado[];
}) {
  const porId = new Map(preguntas.map((q) => [q.id, q]));
  return (
    <section aria-labelledby="res-informe">
      <h3 id="res-informe" className="mb-3 text-4 font-bold text-brand-ink">
        <i className="bi bi-file-earmark-person-fill" aria-hidden="true" /> Informe de encuestados
      </h3>
      <ul className="flex flex-col gap-2">
        {filas.map((fila, i) => {
          const nombre = fila.respondent || 'Usuario';
          return (
            <li key={i} className="rounded-sm border border-line p-3">
              <p className="mb-1.5 flex items-center gap-2">
                <Avatar nombre={nombre} tamano={28} />
                <strong className="text-3 text-brand-ink">{nombre}</strong>
                {fila.submitted_at && (
                  <span className="ml-auto text-1 text-muted">{Utils.fmtDate(fila.submitted_at)}</span>
                )}
              </p>
              <dl className="flex flex-col gap-0.5">
                {(fila.answers ?? []).map((a, j) => {
                  const q = porId.get(a.question_id);
                  const valores = Array.isArray(a.answer_value) ? a.answer_value : [a.answer_value];
                  return (
                    <div key={j} className="flex flex-wrap gap-x-1.5 text-2">
                      <dt className="text-muted">{q ? q.question_text : 'Pregunta'}:</dt>
                      <dd className="font-semibold text-ink">
                        {valores.map((v) => etiquetaDelValor(q, String(v))).join(', ')}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
